import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { useAuth } from '../context/AuthContext';
import { ApiResponse, ColaAudiosEstado } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const POLLING_MS = 15000;

const formatFecha = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const formatMinutos = (min: number): string => {
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h ${m}min`;
};

/**
 * Vista operativa del worker de audios (transcripción + análisis IA) — solo
 * Gerente/Supervisor la ven, igual que la restricción del backend
 * (requireRol en audioRoutes.ts). Un Operador solo ve sus propios audios en
 * la tabla de abajo, sin esta foto del sistema completo.
 */
export const ColaAudiosPanel: React.FC = () => {
    const { usuario } = useAuth();
    const [data, setData] = useState<ColaAudiosEstado | null>(null);
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(true);

    const puedeVer = usuario?.rol === 'gerente' || usuario?.rol === 'supervisor';

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_BASE}/audios/cola/estado`);
            if (res.status === 403) { setVisible(false); return; }
            const json: ApiResponse<ColaAudiosEstado> = await res.json();
            if (json.success && json.data) setData(json.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!puedeVer) return;
        fetchData();
        const interval = setInterval(fetchData, POLLING_MS);
        return () => clearInterval(interval);
    }, [puedeVer, fetchData]);

    if (!puedeVer || !visible || !data) return null;

    const atascada = data.mas_antiguo_pendiente_minutos !== null && data.mas_antiguo_pendiente_minutos > 15;
    const hayErrores = data.errores_recientes.length > 0;

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm mb-4">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold text-slate-800">🎙️ Cola de Audios (worker IA)</h3>
                    <p className="text-xs text-slate-500">
                        {!data.worker_configurado ? (
                            <span className="text-red-600 font-medium">⚠️ Worker sin configurar (falta WHISPER_URL/OLLAMA_URL)</span>
                        ) : data.en_cola === 0 && !hayErrores ? (
                            <span className="text-emerald-600 font-medium">✅ Al día, sin pendientes</span>
                        ) : (
                            'Estado del procesamiento de transcripción y análisis'
                        )}
                    </p>
                </div>
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded"
                >
                    {loading ? '⟳' : '↻'} Refrescar
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <StatBox label="En cola" value={data.en_cola} sub="recibido + procesando" color={data.en_cola > 0 ? 'amber' : 'emerald'} />
                <StatBox
                    label="Más antiguo"
                    value={data.mas_antiguo_pendiente_minutos !== null ? formatMinutos(data.mas_antiguo_pendiente_minutos) : '—'}
                    sub={atascada ? 'revisar, puede estar trabada' : 'esperando'}
                    color={atascada ? 'red' : 'emerald'}
                    small
                />
                <StatBox label="Pendiente revisión" value={data.conteos.pendiente_revision} sub="ya analizados" color="amber" />
                <StatBox label="Con error" value={data.conteos.error} sub="necesitan reprocesar" color={data.conteos.error > 0 ? 'red' : 'emerald'} />
            </div>

            {hayErrores && (
                <div>
                    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        ❌ Audios con error (últimos {data.errores_recientes.length})
                    </h4>
                    <div className="space-y-1 max-h-[240px] overflow-y-auto scrollbar-thin">
                        {data.errores_recientes.map(e => (
                            <div key={e.id} className="flex items-start gap-2 p-2 border rounded bg-red-50 border-red-200">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-800">
                                        #{e.id} {e.cliente_nombre}
                                        <span className="text-xs text-slate-500 font-normal"> · {formatFecha(e.actualizado_en)}</span>
                                    </div>
                                    <div className="text-xs text-slate-600 break-words">{e.error_detalle ?? 'Sin detalle'}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                        Reprocesalos desde la lista de audios (acción "Reprocesar" en cada fila con estado Error).
                    </p>
                </div>
            )}
        </div>
    );
};

const StatBox: React.FC<{ label: string; value: number | string; sub: string; color: 'red' | 'amber' | 'emerald'; small?: boolean }> = ({
    label, value, sub, color, small
}) => {
    const colors = {
        red: 'bg-red-50 border-red-200 text-red-800',
        amber: 'bg-amber-50 border-amber-200 text-amber-800',
        emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    };
    return (
        <div className={`p-2 rounded-lg border ${colors[color]}`}>
            <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
            <div className={small ? 'text-base font-bold' : 'text-xl font-bold'}>{value}</div>
            {sub && <div className="text-[10px] opacity-60">{sub}</div>}
        </div>
    );
};
